// Update banner — listens to main-process auto-update events and shows a
// non-blocking notice at the top of the dashboard. Five states:
//   - none      → no banner
//   - available → "Update available: v0.x.y" + Download button (Electron) or
//                 link-out (mobile/relay — no in-process installer)
//   - downloaded → "Ready to install" + Install button (electron-updater's
//                  per-platform LinuxUpdater/AppImageUpdater/NsisUpdater)
//   - install-failed → install never started or pkexec/UAC was cancelled.
//                  Shows the exact `sudo dpkg -i <path>` / `open <path>` /
//                  Windows installer-folder command the user can run instead.
//
// Two signals feed the 'available' transition:
//   1. electron-updater IPC events from the main process (preferred — only
//      fires inside the Electron desktop shell).
//   2. /api/updates/check polling against the GH releases API (fallback —
//      keeps the mobile/relay users informed when there's no main-process
//      updater running). The server-side proxy caches for 6h so we never
//      hammer GitHub.
import { useEffect, useState } from 'react';
import { isCloud } from '@/lib/surface';
import { ArrowDownToLine, Check, Copy, Loader2, X } from 'lucide-react';
import { Button } from '@onestone/ui/button';
import { api } from '@/lib/api';
import { writeClipboard } from '@/lib/electron';
import { FEEDBACK_DISPLAY_MS, UPDATE_CHECK_POLL_MS } from '@/lib/uiConstants';
import type { UpdateCheckResponse } from '@onestone/shared/responses';

type Stage = 'none' | 'available' | 'downloading' | 'downloaded' | 'install-failed' | 'restarting';
type Source = 'electron' | 'web';

interface BannerState {
  stage: Stage;
  version: string;
  error: string;
  source: Source;
  url: string;
  /** Absolute path to the downloaded installer — set on 'update-downloaded'
   *  so the install-failed fallback can show a copy-pasteable manual command. */
  downloadedPath: string;
}

const INITIAL: BannerState = {
  stage: 'none',
  version: '',
  error: '',
  source: 'electron',
  url: '',
  downloadedPath: '',
};
const DISMISS_KEY = 'onestone-update-dismissed';

/** Best-effort OS sniff for the right manual-install command in the fallback
 *  UI. We don't have access to process.platform from the renderer, so go by
 *  the path extension — `.deb`/`.AppImage` mean Linux, `.exe` Windows.
 *  Defaults to "open this file" if the extension is unfamiliar. */
function manualInstallCommand(installerPath: string): string {
  if (!installerPath) return '';
  if (installerPath.endsWith('.deb')) return `sudo dpkg -i "${installerPath}"`;
  if (installerPath.endsWith('.AppImage')) return `chmod +x "${installerPath}" && "${installerPath}"`;
  if (installerPath.endsWith('.exe')) return `start "" "${installerPath}"`;
  return `open "${installerPath}"`;
}

/** Human-readable rate, e.g. "4.2 MB/s". */
function formatRate(bytesPerSecond: number): string {
  if (!bytesPerSecond || bytesPerSecond <= 0) return '';
  const mb = bytesPerSecond / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB/s`;
  return `${Math.round(bytesPerSecond / 1024)} KB/s`;
}

export function UpdateBanner() {
  // Cloud bundle: nothing to update (worker deploys are server-side) — bail.
  // Every desktop-backend client renders the banner: the Electron window via
  // IPC events, and the phone over the relay via the /api/updates/check +
  // /api/system/update/* REST fallback below. (The old !isElectron() guard
  // dead-coded that documented REST path for ALL browsers — fixed 2026-09-24.)
  if (isCloud()) return null;

  const [state, setState] = useState<BannerState>(INITIAL);
  const [copied, setCopied] = useState(false);
  // Live download progress (desktop electron path only). null → no percentage
  // yet (show an indeterminate spinner until the first progress event arrives).
  const [progress, setProgress] = useState<{ percent: number; rate: number } | null>(null);

  useEffect(() => {
    const electronApi = typeof window === 'undefined' ? null : window.electronAPI;

    // Read dismissed-version once at mount — if the user already X'd the
    // banner for v0.6.8 we don't re-show it until v0.6.9 ships.
    const dismissedVersion = (() => {
      try {
        return window.localStorage.getItem(DISMISS_KEY) ?? '';
      } catch {
        return '';
      }
    })();

    // Electron path — IPC events fire only inside the desktop shell.
    let offAvail: (() => void) | undefined;
    let offProgress: (() => void) | undefined;
    let offDone: (() => void) | undefined;
    let offError: (() => void) | undefined;
    if (electronApi) {
      offAvail = electronApi.onUpdateAvailable((info) => {
        const version = info.version ?? '';
        if (version && version === dismissedVersion) return;
        setState({
          stage: 'available',
          version,
          error: '',
          source: 'electron',
          url: '',
          downloadedPath: '',
        });
      });
      offProgress = electronApi.onUpdateDownloadProgress((info) => {
        setProgress({ percent: info.percent, rate: info.bytesPerSecond });
      });
      offDone = electronApi.onUpdateDownloaded((info) => {
        setProgress(null);
        setState({
          stage: 'downloaded',
          version: info.version ?? '',
          error: '',
          source: 'electron',
          url: '',
          downloadedPath: info.downloadedPath ?? '',
        });
      });
      offError = electronApi.onUpdateError((info) => {
        setState((prev) => {
          // Download / check errors → inline error on the current banner.
          if (info.stage !== 'install') {
            return { ...prev, error: info.message };
          }
          // Install errors get the dedicated fallback UI with the manual
          // command. Preserve the previously-captured downloadedPath if the
          // main process didn't echo it back this time.
          return {
            ...prev,
            stage: 'install-failed',
            error: info.message,
            downloadedPath: info.downloadedPath ?? prev.downloadedPath,
          };
        });
      });
    }

    // Web/relay fallback — server-side proxy with 30-min cache. Skip when
    // the electron IPC path is live, since electron-updater hits the same data
    // and offers a richer Download/Install flow.
    //
    // Polls every 30 min so a phone left open all day still surfaces a new
    // version published after the initial mount. The server cache means these
    // calls are cheap (no extra GH API hits).
    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    if (!electronApi) {
      const poll = async () => {
        try {
          const data = await api<UpdateCheckResponse>('/api/updates/check');
          if (cancelled) return;
          if (data.updateAvailable && data.latest && data.latest !== dismissedVersion) {
            setState({
              stage: 'available',
              version: data.latest,
              error: '',
              source: 'web',
              url: data.url ?? '',
              downloadedPath: '',
            });
          }
        } catch {
          /* offline / rate-limited — silently leave the banner hidden */
        }
      };
      void poll(); // immediate check on mount
      pollTimer = setInterval(() => void poll(), UPDATE_CHECK_POLL_MS);
    }

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
      offAvail?.();
      offProgress?.();
      offDone?.();
      offError?.();
    };
  }, []);

  if (state.stage === 'none') return null;

  async function handleDownload() {
    setProgress(null);
    setState((prev) => ({ ...prev, stage: 'downloading', error: '' }));
    const electronApi = window.electronAPI;
    if (electronApi) {
      // Desktop Electron path — IPC
      const result = await electronApi.downloadUpdate();
      if (!result.ok) {
        setState((prev) => ({ ...prev, stage: 'available', error: result.error ?? 'Download failed' }));
      }
      // 'downloaded' transition fires from the IPC event, not here.
    } else {
      // Mobile/relay path — REST API
      try {
        const result = await api<{ ok: boolean; error?: string }>('/api/system/update/download', { method: 'POST' });
        if (!result.ok) {
          setState((prev) => ({ ...prev, stage: 'available', error: result.error ?? 'Download failed' }));
        } else {
          // Poll for download completion — on mobile we don't get IPC events.
          // The download runs server-side; we transition to 'downloaded' state.
          setState((prev) => ({ ...prev, stage: 'downloaded' }));
        }
      } catch {
        setState((prev) => ({ ...prev, stage: 'available', error: 'Download request failed' }));
      }
    }
  }

  async function handleInstall() {
    const electronApi = window.electronAPI;
    if (electronApi) {
      // Desktop Electron path — IPC
      const result = await electronApi.installUpdate();
      if (!result.ok) {
        setState((prev) => ({
          ...prev,
          stage: 'install-failed',
          error: result.error ?? 'Install failed',
        }));
      }
    } else {
      // Mobile/relay path — REST API
      setState((prev) => ({ ...prev, stage: 'restarting' }));
      try {
        await api<{ ok: boolean }>('/api/system/update/install', { method: 'POST' });
        // App will restart — connection drops and PWA reconnects
      } catch {
        setState((prev) => ({ ...prev, stage: 'install-failed', error: 'Install request failed' }));
      }
    }
  }

  async function copyManualCommand() {
    const cmd = manualInstallCommand(state.downloadedPath);
    if (!cmd) return;
    try {
      await writeClipboard(cmd);
      setCopied(true);
      window.setTimeout(() => setCopied(false), FEEDBACK_DISPLAY_MS);
    } catch {
      /* clipboard write blocked — user can still select-and-copy from the <code> */
    }
  }

  function dismiss() {
    // Persist the dismissed version so the banner doesn't pop back on the
    // next page load — but only suppresses *this* version. A newer release
    // re-shows the banner.
    try {
      if (state.version) window.localStorage.setItem(DISMISS_KEY, state.version);
    } catch {
      /* localStorage may be disabled — accept the re-show on next mount */
    }
    setState(INITIAL);
  }

  // The install-failed state gets its own multi-line layout because the
  // manual command + copy button need more room than the inline banner.
  if (state.stage === 'install-failed') {
    const cmd = manualInstallCommand(state.downloadedPath);
    return (
      <div className="flex flex-col gap-2 border-b border-amber-500/30 bg-amber-500/5 px-4 py-3 text-xs text-foreground">
        <div className="flex items-start gap-3">
          <ArrowDownToLine className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
          <div className="flex-1">
            <div className="font-medium">Couldn&rsquo;t install v{state.version} automatically</div>
            <div className="mt-0.5 text-muted-foreground">{state.error}</div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss update banner"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
        {cmd && (
          <div className="flex items-center gap-2 pl-6">
            <code className="flex-1 truncate rounded bg-muted px-2 py-1 font-mono text-2xs">{cmd}</code>
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1 text-xs"
              onClick={copyManualCommand}
              aria-label="Copy manual install command"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 border-b border-emerald-500/30 bg-emerald-500/5 px-4 py-2 text-xs text-foreground">
      <ArrowDownToLine className="h-3.5 w-3.5 text-emerald-500" />
      <div className="flex-1">
        {state.stage === 'available' && (
          <span>
            Update available: <span className="font-medium">v{state.version}</span>
          </span>
        )}
        {state.stage === 'downloading' && (
          <span className="flex items-center gap-2">
            <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
            <span className="shrink-0">
              Downloading v{state.version}
              {progress ? ` — ${progress.percent}%` : '…'}
              {progress && progress.rate > 0 ? (
                <span className="text-muted-foreground"> ({formatRate(progress.rate)})</span>
              ) : null}
            </span>
            {progress ? (
              <span className="h-1 flex-1 overflow-hidden rounded-full bg-muted" aria-hidden="true">
                <span
                  className="block h-full rounded-full bg-emerald-500 transition-[width] duration-300"
                  style={{ width: `${progress.percent}%` }}
                />
              </span>
            ) : null}
          </span>
        )}
        {state.stage === 'downloaded' && (
          <span>
            v{state.version} is ready. <span className="text-muted-foreground">Click Install to apply.</span>
          </span>
        )}
        {state.stage === 'restarting' && (
          <span className="flex items-center gap-1.5">
            <Loader2 className="h-3 w-3 animate-spin" />
            Restarting&hellip; reconnecting shortly
          </span>
        )}
        {state.error && <span className="ml-2 text-amber-600 dark:text-amber-400">· {state.error}</span>}
      </div>
      {state.stage === 'available' && (
        <Button size="sm" className="h-7 text-xs" onClick={handleDownload}>
          Download
        </Button>
      )}
      {state.stage === 'downloaded' && (
        <Button size="sm" className="h-7 text-xs" onClick={handleInstall}>
          Install &amp; restart
        </Button>
      )}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss update banner"
        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
